import type { CSSProperties, ReactNode } from 'react'
import { cva } from 'class-variance-authority'
import { IncludedIcon } from '#icons'
import { cn } from '#utils'
import type { KeyPoint } from '@lgc/database/types/domains/subscription'

/**
 * KeyBenefitsList
 *
 * Renders a subscription's key benefits (title + optional description) as a
 * checklist. Used on the public purchase pages (mobile browse, web review) and
 * in the admin "Web Details" preview. The list is presentational only — callers
 * decide whether to pass admin-authored `keyBenefits` or the config-derived
 * fallback from `deriveKeyBenefits()`.
 *
 * Empty titles are filtered and empty descriptions are hidden, so the
 * title-only derived fallback (`{ title, description: '' }`) renders cleanly.
 */

const listVariants = cva('flex flex-col', {
  variants: { size: { sm: 'gap-2', md: 'gap-3' } },
  defaultVariants: { size: 'md' },
})

const iconVariants = cva('mt-0.5 flex-shrink-0', {
  variants: {
    size: { sm: 'size-4', md: 'size-5' },
    tone: { default: 'text-butter-gray-600', muted: 'text-butter-gray-400' },
  },
  defaultVariants: { size: 'md', tone: 'default' },
})

const titleVariants = cva('font-semibold text-butter-gray-900', {
  variants: { size: { sm: 'text-xs', md: 'text-sm' } },
  defaultVariants: { size: 'md' },
})

const descVariants = cva('', {
  variants: {
    size: { sm: 'text-xs', md: 'text-sm' },
    tone: { default: 'text-butter-gray-500', muted: 'text-butter-gray-400' },
  },
  defaultVariants: { size: 'md', tone: 'default' },
})

export interface KeyBenefitsListProps {
  benefits: KeyPoint[]
  /** Type scale + spacing. `sm` for compact contexts (admin preview), `md` for purchase pages. */
  size?: 'sm' | 'md'
  /** Icon + description tint. `muted` reads lighter for secondary contexts. */
  tone?: 'default' | 'muted'
  /**
   * Clamp each description to N lines (keeps cards uniform when copy runs long).
   * The purchase surfaces (mobile browse, web review) and the admin preview all
   * pass `2` — an authored description can run to several hundred characters, and
   * without a clamp a full set of benefits pushes a mobile CTA well below the
   * fold. Keep them in step: the admin preview is the only place an author sees
   * what a member will get.
   */
  clampDescription?: number
  /** Shown when there are no titled benefits (ignored when `hideWhenEmpty`). */
  emptyMessage?: string
  /** Render nothing (instead of `emptyMessage`) when empty — for collapsing a whole section. */
  hideWhenEmpty?: boolean
  /** Override the leading `IncludedIcon`. */
  icon?: ReactNode
  className?: string
}

export function KeyBenefitsList({
  benefits,
  size = 'md',
  tone = 'default',
  clampDescription,
  emptyMessage = 'No key benefits added',
  hideWhenEmpty = false,
  icon,
  className,
}: KeyBenefitsListProps) {
  const visible = benefits.filter((b) => b.title.trim())

  if (visible.length === 0) {
    if (hideWhenEmpty) return null
    return <p className={cn('text-xs text-butter-gray-400', className)}>{emptyMessage}</p>
  }

  const clampStyle: CSSProperties | undefined = clampDescription
    ? { display: '-webkit-box', WebkitLineClamp: clampDescription, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
    : undefined

  return (
    <ul className={cn(listVariants({ size }), className)}>
      {visible.map((benefit, i) => (
        <li key={i} className="flex items-start gap-2">
          {icon ?? <IncludedIcon className={iconVariants({ size, tone })} />}
          <div>
            <p className={titleVariants({ size })}>{benefit.title}</p>
            {benefit.description && (
              <p className={descVariants({ size, tone })} style={clampStyle}>
                {benefit.description}
              </p>
            )}
          </div>
        </li>
      ))}
    </ul>
  )
}
